/**
 * Ask mode API — Track C2
 *
 * Sends a message to the ASK backend and returns the raw Response so that
 * useChat can stream SSE chunks incrementally.
 *
 * Auth: Firebase ID token attached as `Authorization: Bearer <token>`,
 *       exactly the same way jax.ts does it. We can't use jax here because
 *       jax consumes the full response body, which would break SSE streaming.
 *
 *                                                                         .
 * DEV HINT: When the backend runs with DEV_MODE=true it cannot verify Firebase
 * ID tokens, so it trusts a `x-sensei-user-id` header (sent here) to know who
 * the user is. In production the backend ignores this header and always uses
 * the verified token — the header is harmless to send in both environments.
 *
 * Endpoint: https://sensei-ask.scmdojo.com
 */

import { getIdToken } from "@/platform/firebase/auth";

const ASK_ENDPOINT = process.env.NEXT_PUBLIC_ASK_ENDPOINT ?? "https://sensei-ask.scmdojo.com";

/** Decode the Firebase ID token's payload to get the user's uid. */
function uidFromToken(token: string): string | undefined {
  try {
    const body = token.split(".")[1];
    const json = atob(body.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(body.length / 4) * 4, "="));
    return (JSON.parse(json) as { user_id?: string }).user_id;
  } catch {
    return undefined;
  }
}

export interface SendMessageParams {
  sessionId:   string;
  userQuery:   string;
  stream?:     boolean;
  attachment?: File;
  attachments?: File[];
  /** "fast" | "expert" — selects the backend model + cross-session memory depth */
  mode?:       "fast" | "expert";
  /** Optional AbortSignal so the query can be cancelled mid-stream (Stop button). */
  signal?:     AbortSignal;
}

/** Cross-session chat-memory preferences returned by the backend. */
export interface MemoryPrefs {
  enabled: boolean;
  anchorTimestamp?: string | null;
}

/**
 * POST a message to the ASK endpoint.
 * Returns the raw Response — callers must read the body themselves.
 * Throws on non-2xx responses with the backend's error message when available.
 */
export async function sendMessage(params: SendMessageParams): Promise<Response> {
  const { sessionId, userQuery, stream = true, attachments, mode = "fast", signal } = params;

  // Get Firebase ID token — same approach as jax.ts
  const token = await getIdToken();
  if (!token) {
    throw new Error("Not authenticated. Please sign in before sending a message.");
  }

  const formData = new FormData();
  formData.append("session_id", sessionId);
  formData.append("user_query", userQuery);
  formData.append("stream",     stream ? "true" : "false");
  formData.append("mode",       mode);

  // Send all file attachments
  if (attachments && attachments.length > 0) {
    for (const file of attachments) {
      formData.append("attachments", file);
    }
  }

  const response = await fetch(ASK_ENDPOINT, {
    method:  "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      ...(uidFromToken(token) ? { "x-sensei-user-id": uidFromToken(token)! } : {}),
    },
    body:    formData,
    signal,
  });

  if (!response.ok) {
    let errorMessage = "Failed to get response from server";
    try {
      const errorData = await response.clone().json();
      if (errorData?.error) errorMessage = errorData.error;
    } catch {
      // Body wasn't JSON — use default message
    }
    throw new Error(errorMessage);
  }

  return response;
}

// ── Cross-session memory ────────────────────────────────────────────────────

/** Build an authenticated Authorization header from the current Firebase user. */
async function memoryHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
    ...(uidFromToken(token) ? { "x-sensei-user-id": uidFromToken(token)! } : {}),
  };
}

/** Fetch the user's cross-session chat-memory preferences. */
export async function getMemoryPrefs(): Promise<MemoryPrefs> {
  const res = await fetch(`${ASK_ENDPOINT}/memory`, { headers: await memoryHeaders() });
  if (!res.ok) throw new Error("Failed to load memory settings");
  const data = (await res.json()) as MemoryPrefs;
  return { enabled: data.enabled !== false, anchorTimestamp: data.anchorTimestamp ?? null };
}

/** Update cross-session memory ("enable" | "disable" | "clear"). */
export async function updateMemoryPrefs(action: "enable" | "disable" | "clear"): Promise<MemoryPrefs> {
  const formData = new FormData();
  formData.append("action", action);
  const res = await fetch(`${ASK_ENDPOINT}/memory`, {
    method: "POST",
    headers: await memoryHeaders(),
    body: formData,
  });
  if (!res.ok) {
    let msg = "Failed to update memory settings";
    try {
      const errData = await res.clone().json();
      if (errData?.error) msg = errData.error;
    } catch {
      // non-JSON error body
    }
    throw new Error(msg);
  }
  const data = (await res.json()) as MemoryPrefs;
  return { enabled: data.enabled !== false, anchorTimestamp: data.anchorTimestamp ?? null };
}
